import { TableCell, TableRow } from '@talentmesh/ui/components/table';
import { cn } from '@talentmesh/ui/lib/utils';

import { type DragEvent, memo, useMemo, useRef } from 'react';

import { useMRTCompatibleTheme } from '../../lib/mrt-theme';
import {
    type MRT_Cell,
    type MRT_ColumnVirtualizer,
    type MRT_DensityState,
    type MRT_Row,
    type MRT_RowData,
    type MRT_RowVirtualizer,
    type MRT_TableInstance,
    type MRT_VirtualItem,
} from '../../types';
import { type TableProps, type TableTrProps } from '../../types/mrt-ui-props';
import { mergeCssVars, resolveThemeStyle } from '../../utils/mrt-style';
import { getIsRowSelected } from '../../utils/row.utils';
import { parseFromValuesOrFunc } from '../../utils/utils';
import { Memo_MRT_TableBodyCell, MRT_TableBodyCell } from './MRT_TableBodyCell';
import classes from './MRT_TableBodyRow.module.css';
import { MRT_TableDetailPanel } from './MRT_TableDetailPanel';

interface Props<TData extends MRT_RowData> extends TableTrProps {
    columnVirtualizer?: MRT_ColumnVirtualizer;
    numRows?: number;
    pinnedRowIds?: string[];
    renderedRowIndex?: number;
    row: MRT_Row<TData>;
    rowVirtualizer?: MRT_RowVirtualizer;
    table: MRT_TableInstance<TData>;
    tableProps: Partial<TableProps>;
    virtualRow?: MRT_VirtualItem;
}

export const MRT_TableBodyRow = <TData extends MRT_RowData>({
    children,
    columnVirtualizer,
    numRows,
    pinnedRowIds,
    renderedRowIndex = 0,
    row,
    rowVirtualizer,
    table,
    tableProps,
    virtualRow,
    ...rest
}: Props<TData>) => {
    const {
        getState,
        options: {
            enableRowOrdering,
            enableRowPinning,
            enableStickyFooter,
            enableStickyHeader,
            layoutMode,
            mantineTableBodyRowProps,
            memoMode,
            renderDetailPanel,
            rowPinningDisplayMode,
        },
        refs: { tableFooterRef, tableHeadRef },
        setHoveredRow,
    } = table;
    const theme = useMRTCompatibleTheme();
    const { density, draggingColumn, draggingRow, editingCell, editingRow, hoveredRow, isFullScreen, rowPinning } =
        getState();

    const visibleCells = row.getVisibleCells();

    const { virtualColumns, virtualPaddingLeft, virtualPaddingRight } = columnVirtualizer ?? {};

    const isRowSelected = getIsRowSelected({ row, table });
    const isRowPinned = enableRowPinning && row.getIsPinned();
    const isRowStickyPinned = isRowPinned && rowPinningDisplayMode?.includes('sticky') && 'sticky';
    const isDraggingRow = draggingRow?.id === row.id;
    const isHoveredRow = hoveredRow?.id === row.id;

    const tableRowProps = {
        ...parseFromValuesOrFunc(mantineTableBodyRowProps, {
            renderedRowIndex,
            row,
            table,
        }),
        ...rest,
    };

    const {
        __vars: rowVarsExtra,
        className: rowClassName,
        style: rowStyle,
        ...restRowProps
    } = tableRowProps as {
        __vars?: Record<string, number | string | undefined>;
    } & TableTrProps;

    const [bottomPinnedIndex, topPinnedIndex] = useMemo(() => {
        if (!enableRowPinning || !isRowStickyPinned || !pinnedRowIds || !row.getIsPinned()) return [];
        return [[...pinnedRowIds].reverse().indexOf(row.id), pinnedRowIds.indexOf(row.id)];
    }, [pinnedRowIds, rowPinning]);

    const tableHeadHeight = ((enableStickyHeader || isFullScreen) && tableHeadRef.current?.clientHeight) || 0;
    const tableFooterHeight = (enableStickyFooter && tableFooterRef.current?.clientHeight) || 0;

    const defaultRowHeightByDensity: Record<MRT_DensityState, number> = {
        lg: 61,
        md: 53,
        sm: 45,
        xl: 69,
        xs: 37,
    };

    const rowHeight =
        // @ts-ignore
        parseInt((rowStyle as { height?: string })?.height, 10) ||
        (defaultRowHeightByDensity[density] ?? defaultRowHeightByDensity['md']);

    const handleDragEnter = (_e: DragEvent) => {
        if (enableRowOrdering && draggingRow) {
            setHoveredRow(row);
        }
    };

    const rowRef = useRef<HTMLTableRowElement>(null);

    let striped = tableProps.striped as boolean | string;

    if (striped) {
        if (striped === true) {
            striped = 'odd';
        }
        if (striped === 'odd' && renderedRowIndex % 2 !== 0) {
            striped = false;
        }
        if (striped === 'even' && renderedRowIndex % 2 === 0) {
            striped = false;
        }
    }

    const rowCssVars = mergeCssVars({
        ...rowVarsExtra,
        '--mrt-pinned-row-bottom':
            !virtualRow && bottomPinnedIndex !== undefined && isRowPinned
                ? `${bottomPinnedIndex * rowHeight + (enableStickyFooter ? tableFooterHeight - 1 : 0)}`
                : undefined,
        '--mrt-pinned-row-top': virtualRow
            ? undefined
            : topPinnedIndex !== undefined && isRowPinned
              ? `${topPinnedIndex * rowHeight + (enableStickyHeader || isFullScreen ? tableHeadHeight - 1 : 0)}`
              : undefined,
        '--mrt-virtual-row-start': virtualRow ? `${virtualRow.start}` : undefined,
    });

    return (
        <>
            <TableRow
                data-dragging-row={isDraggingRow || undefined}
                data-hover
                data-hovered-row-target={isHoveredRow || undefined}
                data-index={renderDetailPanel ? renderedRowIndex * 2 : renderedRowIndex}
                data-row-pinned={isRowStickyPinned || isRowPinned || undefined}
                data-selected={isRowSelected || undefined}
                data-striped={striped}
                onDragEnter={handleDragEnter}
                ref={(node: HTMLTableRowElement | null) => {
                    if (node) {
                        rowRef.current = node;
                        rowVirtualizer?.measureElement(node);
                    }
                }}
                {...restRowProps}
                className={cn(
                    classes.root,
                    layoutMode?.startsWith('grid') && classes['root-grid'],
                    virtualRow && classes['root-virtualized'],
                    rowClassName,
                )}
                style={{
                    ...rowCssVars,
                    ...resolveThemeStyle(rowStyle as any, theme),
                }}
            >
                {virtualPaddingLeft ? (
                    <TableCell
                        aria-hidden
                        className="border-0 p-0"
                        style={{
                            display: 'flex',
                            minWidth: virtualPaddingLeft,
                            width: virtualPaddingLeft,
                        }}
                    />
                ) : null}
                {children
                    ? children
                    : (virtualColumns ?? row.getVisibleCells()).map((cellOrVirtualCell, renderedColumnIndex) => {
                          let cell = cellOrVirtualCell as MRT_Cell<TData>;
                          if (columnVirtualizer) {
                              renderedColumnIndex = (cellOrVirtualCell as MRT_VirtualItem).index;
                              cell = visibleCells[renderedColumnIndex];
                          }
                          const cellProps = {
                              cell,
                              numRows,
                              renderedColumnIndex,
                              renderedRowIndex,
                              rowRef: rowRef as React.RefObject<HTMLTableRowElement>,
                              table,
                              virtualCell: columnVirtualizer ? (cellOrVirtualCell as MRT_VirtualItem) : undefined,
                          };
                          return memoMode === 'cells' &&
                              cell.column.columnDef.columnDefType === 'data' &&
                              !draggingColumn &&
                              !draggingRow &&
                              editingCell?.id !== cell.id &&
                              editingRow?.id !== row.id ? (
                              <Memo_MRT_TableBodyCell key={cell.id} {...cellProps} />
                          ) : (
                              <MRT_TableBodyCell key={cell.id} {...cellProps} />
                          );
                      })}
                {virtualPaddingRight ? (
                    <TableCell
                        aria-hidden
                        className="border-0 p-0"
                        style={{
                            display: 'flex',
                            minWidth: virtualPaddingRight,
                            width: virtualPaddingRight,
                        }}
                    />
                ) : null}
            </TableRow>
            {renderDetailPanel && !row.getIsGrouped() && (
                <MRT_TableDetailPanel
                    parentRowRef={rowRef as React.RefObject<HTMLTableRowElement>}
                    renderedRowIndex={renderedRowIndex}
                    row={row}
                    rowVirtualizer={rowVirtualizer}
                    striped={striped}
                    table={table}
                    virtualRow={virtualRow}
                />
            )}
        </>
    );
};

export const Memo_MRT_TableBodyRow = memo(
    MRT_TableBodyRow,
    (prev, next) => prev.row === next.row,
) as typeof MRT_TableBodyRow;
